import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { MoreHorizontalIcon, MapPinIcon, CheckCircle2Icon, PlusIcon, MicIcon, SignalIcon, WifiIcon, BatteryFullIcon } from 'lucide-react';
import { STREETLIGHT_IMG } from './RequestsDashboardMock';
const EASE = [0.23, 1, 0.32, 1] as const;
export function RequestsPhoneMock({
  isFR
}: {
  isFR: boolean;
}) {
  const t = (fr: string, en: string) => isFR ? fr : en;
  const reduce = useReducedMotion();
  const reveal = (i: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 6
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.25,
      ease: EASE,
      delay: 0.35 + i * 0.12
    }
  };
  return <div className="w-[268px] rounded-[2.6rem] border-[7px] border-[#16171D] bg-white shadow-[0_30px_70px_-25px_rgba(18,18,40,0.35)] overflow-hidden" aria-label={t('Application mobile — conversation avec ATNA', 'Mobile app — conversation with ATNA')} data-id="element-6415" data-mp="lgt4nxiothu6">
      <div className="flex items-center justify-between px-5 pt-3 pb-1 font-sans text-[11px] font-semibold text-[#121212]" data-id="element-6416" data-mp="1j2zyo8vrhpva">
        9:41
        <span className="flex items-center gap-1" aria-hidden="true" data-id="element-6417" data-mp="1eb8imsa8f2je">
          <SignalIcon className="w-3 h-3" strokeWidth={2.4} data-id="element-6418" data-mp="19cx80fc44o04" />
          <WifiIcon className="w-3 h-3" strokeWidth={2.4} data-id="element-6419" data-mp="19dge8ysehnyb" />
          <BatteryFullIcon className="w-4 h-4" strokeWidth={2} data-id="element-6420" data-mp="14evgd60mfgd1" />
        </span>
      </div>

      <div className="flex items-center gap-2.5 px-4 py-2.5" data-id="element-6421" data-mp="222dukwd1xaof">
        <span className="w-7 h-7 rounded-full border-[3px] border-[#4F46E5] border-r-[#C7CAF7]" aria-hidden="true" data-id="element-6422" data-mp="21zi05i0ex44q" />
        <span className="flex-1" data-id="element-6423" data-mp="1s3embew77h1y">
          <span className="block font-sans text-[12.5px] font-semibold text-[#121212] leading-tight" data-id="element-6424" data-mp="1s2n4d6vixhu5">ATNA</span>
          <span className="block font-sans text-[9.5px] text-[#7A8194]" data-id="element-6425" data-mp="1n3sbtd7r8ewu">{t('Assistant citoyen', 'Resident assistant')}</span>
        </span>
        <MoreHorizontalIcon className="w-4 h-4 text-[#121212]" aria-hidden="true" data-id="element-6426" data-mp="gm57j5o0ld6z" />
      </div>

      <div className="px-3.5 pb-3 space-y-2.5 font-sans" data-id="element-6427" data-mp="brrg1ech3q7l">
        <motion.p {...reveal(0)} className="ml-auto max-w-[80%] rounded-xl bg-[#EEF0FF] px-3 py-2 text-[10.5px] text-[#3730A3] leading-snug" data-id="element-6428" data-mp="2zwona7auogch">
          {t('Un lampadaire ne fonctionne plus sur la rue des Érables.', 'A streetlight is no longer working on rue des Érables.')}
        </motion.p>
        <motion.p {...reveal(1)} className="max-w-[92%] rounded-xl bg-[#F6F7F9] px-3 py-2 text-[10.5px] text-[#121212] leading-snug" data-id="element-6429" data-mp="2uuzaon7zbuma">
          {t('J’ai trouvé l’emplacement et déterminé qu’il s’agit d’une demande d’éclairage public. Je peux la transmettre au Service des travaux publics.', 'I found the location and identified this as a street lighting request. I can send it to public works.')}
        </motion.p>
        <motion.div {...reveal(2)} className="rounded-xl border border-[#E8E9EE] overflow-hidden" data-id="element-6430" data-mp="1x8idrj35rj12">
          <img src={STREETLIGHT_IMG} alt={t('Lampadaire à la tombée du jour', 'Streetlight at dusk')} className="w-full h-[84px] object-cover" data-id="element-6431" data-mp="227ljz6uvjvam" />
          <div className="flex items-center justify-between px-2.5 py-2" data-id="element-6432" data-mp="1iml2g706c1ax">
            <span data-id="element-6433" data-mp="1ekq0d0af5jw3">
              <span className="block text-[10.5px] font-semibold text-[#121212]" data-id="element-6434" data-mp="1el9iah02k8a4">{t('Éclairage public', 'Street lighting')}</span>
              <span className="block text-[9.5px] text-[#7A8194]" data-id="element-6435" data-mp="1slaj5wgc3fpl">Rue des Érables, Brossard</span>
            </span>
            <MapPinIcon className="w-3.5 h-3.5 text-[#121212]" strokeWidth={1.8} aria-hidden="true" data-id="element-6436" data-mp="1nn92tjaaluht" />
          </div>
        </motion.div>
        <motion.div {...reveal(3)} className="flex gap-2 rounded-xl bg-[#ECF8F0] px-2.5 py-2.5" data-id="element-6437" data-mp="1xjvlq2j26bh7">
          <CheckCircle2Icon className="w-4 h-4 text-[#22A55B] shrink-0 mt-0.5" strokeWidth={1.8} aria-hidden="true" data-id="element-6438" data-mp="2baqn6tr89qar" />
          <p className="text-[10px] text-[#121212] leading-snug" data-id="element-6439" data-mp="26cp62edd411x">
            {t('Votre demande a été soumise.', 'Your request has been submitted.')}
            <br data-id="element-6440" data-mp="2g9k46w5q3zfo" />
            {t('Numéro : 25473', 'Number: #25473')}
            <br data-id="element-6441" data-mp="2gben64xfphh4" />
            {t('Vous serez informé des prochaines étapes.', 'You will be informed of the next steps.')}
          </p>
        </motion.div>
      </div>

      <div className="flex items-center gap-2 border-t border-[#EEF0F4] px-3.5 py-3" data-id="element-6442" data-mp="iky3t2kedoye">
        <PlusIcon className="w-4 h-4 text-[#121212]" aria-hidden="true" data-id="element-6443" data-mp="ncg4dpiidniu" />
        <span className="flex-1 flex items-center justify-between rounded-full border border-[#E8E9EE] px-3 py-1.5 font-sans text-[9.5px] text-[#9AA0AE]" data-id="element-6444" data-mp="sbt0l0v9s6y3">
          {t('Posez une question à ATNA…', 'Ask ATNA a question…')}
          <MicIcon className="w-3 h-3 text-[#121212]" aria-hidden="true" data-id="element-6445" data-mp="3vrq2ynn6walv" />
        </span>
      </div>
    </div>;
}